import { useState, useEffect, useCallback, useRef } from 'react'
import styled, { css } from 'styled-components'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../supabase'
import { CAPACITE_CAMION, ORDER_TYPES } from './carnetUtils'
import { ecrire, lire } from './erreurs'
import { montant } from './format'
import { adresseDestination } from './logistique'
import CoutLivraison from './CoutLivraison'
import { MODELE_DEVIS, corpsDe, sujetDe, mailtoDe } from './mailModeles'
import {
  t, Stagger, Panel, PanelHead, PanelTitle, Btn, IconBtn, Input, Select, Label, Field, Row,
  Cluster, Pill, Muted, Pre, DefList, DefRow, BackLink, Loading, Empty,
  Spinner, useUI,
} from './ui'
import {
  StatusLabels, StatusTone, STATUS_ORDER, parseDetails, getProduitsListe,
  calcLigne, grandTotalOf, mailDemandeRecue, mailLibre, mailtoDevis,
  isFourrage, fmtDate, fmtDateTime, estChampAdresse, adresseDe, mapsUrl,
  whatsappRelance, mailRelance, telInternational, repartitionVues,
} from './devisUtils'

/* ── Bandeau de tête ── */
const Hero = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-radius: ${t.r};
  padding: 18px 20px;
  margin-bottom: 14px;
  @media (max-width: 700px) { padding: 15px 16px; }
`
const HeroTop = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; flex-wrap: wrap;
`
const HeroName = styled.h1`
  font-family: ${t.serif}; font-size: 1.3rem; font-weight: 660;
  letter-spacing: -0.036em; color: ${t.text}; margin: 0;
  @media (max-width: 700px) { font-size: 1.12rem; }
`
const HeroRef = styled.div`
  display: inline-block;
  font-family: ${t.mono}; font-size: 0.655rem; letter-spacing: 0.09em;
  color: ${t.gold}; margin-bottom: 6px;
  background: ${t.goldWash};
  padding: 2px 7px; border-radius: ${t.rXs};
`
const HeroMeta = styled.div`
  font-family: ${t.font}; font-size: 0.765rem; color: ${t.textSoft}; margin-top: 6px;
  display: flex; flex-wrap: wrap; gap: 4px 14px;
  a { color: ${t.gold}; text-decoration: none; &:hover { text-decoration: underline; } }
`

/* ── Étapes de statut ── */
const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  @media (max-width: 700px) { grid-template-columns: repeat(2, 1fr); }
`
const Step = styled.button`
  text-align: left;
  padding: 9px 11px;
  border-radius: ${t.rSm};
  cursor: pointer;
  font-family: ${t.font};
  transition: background 0.14s, border-color 0.14s;
  border: 1px solid ${(p) => (p.$on ? t.gold : t.line)};
  background: ${(p) => (p.$on ? t.goldWash : t.surface)};
  &:hover { border-color: ${(p) => (p.$on ? t.gold : '#DDDFDA')}; background: ${(p) => (p.$on ? t.goldWash : t.surfaceAlt)}; }
  ${(p) => p.$done && !p.$on && css`background: ${t.surfaceAlt};`}
`
const StepNum = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; border-radius: ${t.rXs};
  font-size: 0.575rem; font-weight: 650; margin-bottom: 6px;
  font-variant-numeric: tabular-nums;
  background: ${(p) => (p.$on ? t.gold : p.$done ? '#C4D6D0' : t.surfaceSunk)};
  color: ${(p) => (p.$on || p.$done ? '#fff' : t.textFaint)};
`
const StepLabel = styled.div`
  font-size: 0.735rem; font-weight: ${(p) => (p.$on ? 570 : 480)};
  letter-spacing: -0.01em;
  color: ${(p) => (p.$on ? t.gold : t.textSoft)};
  line-height: 1.3;
`

/* ── Générateur ── */
const LigneCard = styled.div`
  background: ${t.surfaceAlt};
  border: 1px solid ${t.line};
  border-radius: ${t.rSm};
  padding: 13px 14px;
  margin-bottom: 10px;
`
const LigneName = styled.div`
  font-family: ${t.serif}; font-size: 0.96rem; font-weight: 700;
  color: ${t.text}; margin-bottom: 13px;
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
`
const LigneCalc = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  margin-top: 11px; padding-top: 10px;
  border-top: 1px dashed ${t.line};
  font-family: ${t.font}; font-size: 0.78rem; color: ${t.textSoft};
  flex-wrap: wrap;
  b { font-size: 0.95rem; color: ${t.green}; }
`
const TotalBar = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 14px;
  background: ${t.goldWash};
  border: 1px solid #CFE2DC;
  border-radius: ${t.rSm};
  padding: 11px 15px;
  margin-bottom: 14px;
  span:first-child {
    font-family: ${t.font}; font-size: 0.625rem; font-weight: 600;
    letter-spacing: 0.075em; text-transform: uppercase; color: ${t.gold}; opacity: 0.75;
  }
  span:last-child {
    font-family: ${t.serif}; font-size: 1.06rem; font-weight: 650;
    letter-spacing: -0.032em; font-variant-numeric: tabular-nums; color: ${t.gold};
  }
`
const ViewsBox = styled.div`
  background: ${(p) => (p.$any ? t.greenWash : t.surfaceAlt)};
  border: 1px solid ${(p) => (p.$any ? '#CFE6D8' : t.line)};
  border-radius: ${t.rSm};
  padding: 11px 13px;
`
const ViewLine = styled.div`
  font-family: ${t.font}; font-size: 0.77rem; color: ${t.textSoft};
`
const ViewsHead = styled.div`
  font-family: ${t.font}; font-size: 0.84rem; font-weight: 600;
  letter-spacing: -0.014em; margin-bottom: 7px;
  color: ${(p) => (p.$on ? t.green : t.textSoft)};
`
/* Valeur d'adresse cliquable dans la liste des détails */
const MapLink = styled.a`
  color: ${t.gold};
  text-decoration: none;
  border-bottom: 1px dashed rgba(15,92,77,0.4);
  &:hover { border-bottom-style: solid; }
`

export default function DevisDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast, confirm, announce } = useUI()

  const [quote, setQuote] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [lignes, setLignes] = useState([])
  /* Le modèle intégré ouvre la liste : c'est le mail envoyé neuf fois
     sur dix, il ne faut pas avoir à le rechercher. */
  const [modeles, setModeles] = useState([MODELE_DEVIS])
  const [modeleId, setModeleId] = useState('devis')
  const [attente, setAttente] = useState([])
  const [views, setViews] = useState([])
  const [saving, setSaving] = useState(false)
  const [editCoords, setEditCoords] = useState(null)
  const [commande, setCommande] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('quotes').select('*').eq('id', id).maybeSingle()
    if (!data) { setNotFound(true); setLoading(false); return }
    setQuote(data)

    const { data: att } = await supabase.from('liste_attente')
      .select('id, produit, statut').eq('quote_id', id)
    setAttente(att || [])

    const { data: v } = await supabase.from('quote_views').select('viewed_at').eq('quote_id', id).order('viewed_at', { ascending: false })
    setViews(v || [])

    // Commande engendrée par ce devis, s'il a déjà été envoyé
    /* Pas de maybeSingle : d'anciens renvois ont pu laisser plusieurs
       commandes sur un même devis, et la lecture échouait alors en
       bloc. On prend la plus récente. */
    const { data: cmds } = await supabase.from('orders')
      .select('id, type, status, ca_total_chf, lignes')
      .eq('quote_id', id).order('created_at', { ascending: false }).limit(1)
    const o = cmds?.[0] || null
    setCommande(o)

    /* Le devis déjà envoyé reprend ses propres chiffres. Repartir de
       champs vides obligeait à tout ressaisir pour la moindre retouche,
       et laissait croire que le tonnage envoyé était nul. */
    const dejaEnvoye = (o?.lignes || []).map((l) => ({
      produit: l.produit,
      poids: l.poids_t != null ? String(l.poids_t) : '',
      bottes: l.bottes != null ? String(l.bottes) : '',
      prix: l.prix_100kg != null ? String(l.prix_100kg) : '',
      taxe: l.taxe_100kg ? String(l.taxe_100kg) : '',
    }))
    /* Le brouillon en cours prime sur ce qui a été envoyé : c'est la
       dernière volonté exprimée, même sans nouvel envoi. */
    const brouillon = Array.isArray(data.lignes) ? data.lignes : []
    setLignes(brouillon.length ? brouillon
      : dejaEnvoye.length ? dejaEnvoye
      : getProduitsListe(data).map((p) => ({ produit: p, poids: '', bottes: '', prix: '', taxe: '' })))

    // Marquer comme vu côté admin (retire la surbrillance dans la liste)
    const now = new Date().toISOString()
    supabase.from('quotes').update({ admin_viewed_at: now }).eq('id', id).then(() => {})
    setLoading(false)
  }, [id])

  useEffect(() => { load() }, [load])

  const refreshViews = async () => {
    const { data } = await supabase.from('quote_views').select('viewed_at').eq('quote_id', id).order('viewed_at', { ascending: false })
    setViews(data || [])
    toast('Consultations actualisées', 'info')
  }

  const updateLigne = (i, field, val) =>
    setLignes((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: val } : l)))

  useEffect(() => {
    lire(
      supabase.from('modeles_mail').select('*').eq('actif', true).order('ordre').order('nom'),
      toast, 'chargement des modèles de mail',
    ).then((data) => { if (data) setModeles([MODELE_DEVIS, ...data]) })
  }, [toast])

  /* La saisie se garde toute seule, sans attendre l'envoi. Sans cela,
     un devis rouvert repartait de zéro et les chiffres réfléchis la
     veille étaient perdus. */
  const brouillonEcrit = useRef(null)
  const empreinteLignes = JSON.stringify(lignes)
  useEffect(() => {
    if (loading || !quote?.id || lignes.length === 0) return
    if (brouillonEcrit.current === null) { brouillonEcrit.current = empreinteLignes; return }
    if (brouillonEcrit.current === empreinteLignes) return
    const minuteur = setTimeout(() => {
      brouillonEcrit.current = empreinteLignes
      supabase.from('quotes').update({ lignes }).eq('id', quote.id).then(({ error }) => {
        if (error) { brouillonEcrit.current = null; console.error('brouillon', error) }
      })
    }, 800)
    return () => clearTimeout(minuteur)
  }, [empreinteLignes, quote?.id, loading])  // eslint-disable-line react-hooks/exhaustive-deps

  const modele = modeles.find((m) => (m.cle || m.id) === modeleId) || MODELE_DEVIS
  const grandTotal = grandTotalOf(lignes)
  const lignesRemplies = lignes.length > 0 && lignes.every((l) => l.poids && l.prix)

  /* ── Changement de statut ── */
  const changeStatus = async (status) => {
    if (status === quote.status) return
    const ok = await ecrire(supabase.from('quotes').update({ status }).eq('id', quote.id), toast, 'changement de statut')
    if (!ok) return
    setQuote((q) => ({ ...q, status }))
    toast(`Statut : ${StatusLabels[status]}`)

    if (status === 'received') {
      const ok = await announce({
        icon: '📤',
        tone: 'blue',
        title: 'Prévenir le client ?',
        body: <>Le mail de confirmation « <strong>Votre demande a bien été reçue</strong> » va s'ouvrir dans votre messagerie, prérempli avec le lien de suivi.</>,
        confirmLabel: 'Ouvrir le mail',
      })
      if (ok) window.open(mailDemandeRecue(quote))
    }
  }

  /* ── Suppression ── */
  const remove = async () => {
    const ok = await confirm({
      icon: '🗑️',
      tone: 'red',
      title: 'Supprimer cette demande ?',
      body: <>La demande de <strong>{quote.name}</strong> et la commande associée seront définitivement effacées. Cette action est irréversible.</>,
      confirmLabel: 'Supprimer définitivement',
    })
    if (!ok) return
    await supabase.from('orders').delete().eq('quote_id', quote.id)
    await supabase.from('quotes').delete().eq('id', quote.id)
    toast('Demande et commande associée supprimées')
    navigate('/admin/devis')
  }

  /* ── Sauvegarde des coordonnées ── */
  const saveCoords = async () => {
    await supabase.from('quotes').update({ name: editCoords.name, email: editCoords.email, phone: editCoords.phone }).eq('id', quote.id)
    setQuote((q) => ({ ...q, ...editCoords }))
    setEditCoords(null)
    toast('Coordonnées mises à jour')
  }

  /* ── Génération + envoi ── */
  const sauvegarderEtEnvoyer = async () => {
    setSaving(true)
    const texte = corpsDe(modele, quote, lignes)
    const envoyeLe = new Date().toISOString()

    // 1. Sauvegarder le devis (devis_sent_at sert de repère pour savoir
    //    si les consultations client sont postérieures à l'envoi)
    const enregistre = await ecrire(
      supabase.from('quotes').update({ devis_content: texte, status: 'ready', devis_sent_at: envoyeLe }).eq('id', quote.id),
      toast, 'enregistrement du devis',
    )
    // Sans devis en base, inutile de créer client et commande derrière
    if (!enregistre) { setSaving(false); return }

    // 2. Créer/mettre à jour la fiche client
    const { data: existingClient } = await supabase.from('clients').select('id, type').eq('email', quote.email.toLowerCase()).single()
    let clientId = existingClient?.id
    let clientCree = false
    if (!clientId) {
      const canton = quote.message?.match(/Canton\s*:\s*(.+)/i)?.[1]?.split('\n')[0]?.trim() || ''
      const { data: newClient } = await supabase.from('clients').insert([{
        email: quote.email.toLowerCase(), name: quote.name, phone: quote.phone || '', canton, type: 'prospect',
      }]).select().single()
      clientId = newClient?.id
      clientCree = true
    } else {
      await supabase.from('clients').update({ name: quote.name, phone: quote.phone || '', updated_at: new Date().toISOString() }).eq('id', clientId)
    }

    // 3. Créer la commande
    const poidsTotal = lignes.reduce((acc, l) => acc + (parseFloat(l.poids) || 0), 0)
    const lignesData = lignes.map((l) => {
      const c = calcLigne(l)
      return { produit: l.produit, poids_t: c.p, bottes: l.bottes ? parseInt(l.bottes, 10) : null, prix_100kg: c.pr, taxe_100kg: c.tx, nb_camions: Math.ceil(c.p / CAPACITE_CAMION) || 1, sous_total: c.total }
    })
    /* Renvoyer un devis met à jour sa commande au lieu d'en créer une
       seconde : deux lignes pour un même devis faisaient échouer la
       lecture qui n'en attend qu'une, et le carnet comptait double. */
    const corps = {
      quote_id: quote.id, client_id: clientId,
      lignes: lignesData, poids_total_t: poidsTotal,
      ca_total_chf: grandTotal,
    }
    await ecrire(
      commande?.id
        ? supabase.from('orders').update(corps).eq('id', commande.id)
        : supabase.from('orders').insert([{ ...corps, status: 'sent', type: 'devis' }]),
      toast, 'enregistrement de la commande',
    )

    setQuote((q) => ({ ...q, status: 'ready', devis_content: texte, devis_sent_at: envoyeLe }))
    setSaving(false)

    const ok = await announce({
      icon: '🎉',
      tone: 'green',
      width: '470px',
      title: 'Devis prêt à partir',
      body: (
        <>
          Devis de <strong>{montant(grandTotal)}</strong> enregistré pour <strong>{quote.name}</strong>.<br />
          {clientCree ? 'Fiche client créée' : 'Fiche client mise à jour'} · commande de <strong>{poidsTotal} T</strong> ajoutée au carnet · statut passé à « Devis envoyé ».
          <br /><br />
          Votre messagerie va s'ouvrir avec le mail prérempli.
        </>
      ),
      confirmLabel: 'Ouvrir le mail',
    })
    /* Objet et corps viennent tous deux du modèle retenu, et le corps
       est celui qu'on vient d'archiver : ce qui part est mot pour mot
       ce qui est enregistré sur le devis. */
    if (ok) {
      window.location.href = `mailto:${quote.email}`
        + `?subject=${encodeURIComponent(sujetDe(modele, quote, lignes))}`
        + `&body=${encodeURIComponent(texte)}`
    }
  }

  /* Inscription sur liste d'attente : une ligne par marchandise, car
     le stock se reconstitue produit par produit et rarement en bloc. */
  const inscrireEnAttente = async () => {
    const dejaInscrits = new Set(attente.filter((a) => a.statut === 'attente').map((a) => a.produit))
    const nouvelles = lignes
      .filter((l) => l.produit && !dejaInscrits.has(l.produit))
      .map((l) => ({
        quote_id: quote.id,
        nom: quote.name || 'client',
        email: quote.email || null,
        telephone: quote.phone || null,
        canton: (quote.message?.match(/Canton\s*:\s*(.+)/i)?.[1] || '').split('\n')[0].trim() || null,
        produit: l.produit,
        tonnage: parseFloat(l.poids) || null,
        notes: `Depuis le devis #${quote.id.slice(0, 8).toUpperCase()}`,
      }))

    if (nouvelles.length === 0) {
      return toast('Toutes les marchandises de ce devis sont déjà sur la liste', 'error')
    }
    const ok = await ecrire(
      supabase.from('liste_attente').insert(nouvelles),
      toast, 'inscription sur liste d\'attente',
    )
    if (!ok) return
    toast(`${nouvelles.length} inscription(s) ajoutée(s)`)
    load()
  }

  /* ── Rendu ── */
  if (loading) {
    return (
      <>
        <BackLink onClick={() => navigate('/admin/devis')}>← Retour aux devis</BackLink>
        <Loading rows={3} h="120px" />
      </>
    )
  }

  if (notFound) {
    return (
      <>
        <BackLink onClick={() => navigate('/admin/devis')}>← Retour aux devis</BackLink>
        <Empty icon="🔍" title="Devis introuvable">
          Cette demande a peut-être été supprimée.
        </Empty>
      </>
    )
  }

  const details = parseDetails(quote.message)
  const messageLibre = details.find((d) => d.key.toLowerCase() === 'message')
  const autresDetails = details.filter((d) => d.key.toLowerCase() !== 'message')
  const adresse = adresseDe(autresDetails)
  const waUrl = whatsappRelance(quote)
  const vues = repartitionVues(views.map((v) => v.viewed_at), quote.devis_sent_at)
  const stepIndex = STATUS_ORDER.indexOf(quote.status)

  return (
    <>
      <BackLink onClick={() => navigate('/admin/devis')}>← Retour aux devis</BackLink>

      {/* ── En-tête ── */}
      <Hero>
        <HeroTop>
          <div style={{ minWidth: 0 }}>
            <HeroRef>#{quote.id.slice(0, 8).toUpperCase()}</HeroRef>
            <HeroName>{quote.name}</HeroName>
            <HeroMeta>
              <a href={mailLibre(quote)} title="Écrire un message personnalisé">{quote.email}</a>
              {quote.phone && <a href={`tel:${quote.phone}`}>{quote.phone}</a>}
              <span>Reçue le {fmtDate(quote.created_at, { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            </HeroMeta>
          </div>
          <Cluster $gap="8px" $align="center">
            {attente.some((a) => a.statut === 'attente') && (
              <Pill $tone="amber" $lg>⏳ Liste d'attente</Pill>
            )}
            <Pill $tone={StatusTone[quote.status]} $lg>{StatusLabels[quote.status]}</Pill>
            <IconBtn $sm onClick={remove} title="Supprimer la demande">🗑</IconBtn>
          </Cluster>
        </HeroTop>
      </Hero>

      <Row $cols="1.05fr 0.95fr" $gap="18px">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* ── Statut ── */}
          <Stagger $i={0}>
            <Panel>
              <PanelHead><PanelTitle $sm>Avancement</PanelTitle></PanelHead>
              <Steps>
                {STATUS_ORDER.map((s, i) => (
                  <Step key={s} $on={quote.status === s} $done={i < stepIndex} onClick={() => changeStatus(s)}>
                    <StepNum $on={quote.status === s} $done={i < stepIndex}>{i < stepIndex ? '✓' : i + 1}</StepNum>
                    <StepLabel $on={quote.status === s}>{StatusLabels[s]}</StepLabel>
                  </Step>
                ))}
              </Steps>
            </Panel>
          </Stagger>

          {/* ── Relances ── */}
          <Stagger $i={1}>
            <Panel>
              <PanelHead>
                <PanelTitle $sm>Prévenir le client</PanelTitle>
                <Pill $tone={vues.apres > 0 ? 'green' : vues.total > 0 ? 'amber' : 'neutral'}>
                  {vues.apres > 0 ? 'Devis lu' : vues.total > 0 ? 'Pas lu depuis l’envoi' : 'Jamais consulté'}
                </Pill>
              </PanelHead>

              <Muted style={{ marginBottom: 13 }}>
                {!quote.devis_content
                  ? "Le devis n'a pas encore été envoyé. Ces relances renvoient vers la page de suivi de sa demande."
                  : vues.apres > 0
                  ? `Le client a ouvert son devis ${vues.apres} fois depuis l'envoi. Une relance reste possible si vous attendez sa validation.`
                  : vues.apres === 0
                  ? "Le devis a été envoyé mais le client ne l'a pas encore ouvert. C'est le bon moment pour une relance."
                  : "Envoi non horodaté : impossible de savoir si les consultations sont postérieures au devis."}
              </Muted>

              <Cluster $gap="9px">
                {waUrl ? (
                  <Btn as="a" href={waUrl} target="_blank" rel="noopener noreferrer" $variant="ghost"
                    title={`Ouvrir WhatsApp avec le +${telInternational(quote.phone)}`}>
                    💬 Relancer sur WhatsApp
                  </Btn>
                ) : (
                  <Btn $variant="ghost" disabled title="Aucun numéro de téléphone sur cette demande">
                    💬 Relancer sur WhatsApp
                  </Btn>
                )}
                <Btn as="a" href={mailRelance(quote)} $variant="ghost">
                  📧 Relancer par email
                </Btn>
              </Cluster>

              <Muted style={{ fontSize: '0.73rem', marginTop: 11 }}>
                Les deux messages sont prérédigés avec le lien direct vers son devis. Rien ne part sans votre validation.
              </Muted>
            </Panel>
          </Stagger>

          {/* ── Commande liée ── */}
          {commande && (
            <Stagger $i={2}>
              <Panel>
                <PanelHead>
                  <PanelTitle $sm>Commande au carnet</PanelTitle>
                  <Pill $tone={StatusTone[commande.status] || 'neutral'}>
                    {ORDER_TYPES[commande.type]?.label || '📋 Devis'}
                  </Pill>
                </PanelHead>
                <Muted style={{ marginBottom: 12 }}>
                  Ce devis a engendré une commande de <strong style={{ color: t.text }}>
                  {montant(commande.ca_total_chf)}</strong>. C'est là que se décide
                  la suite : la passer en pro forma une fois le client d'accord, puis en facture
                  après livraison.
                </Muted>
                <Btn $variant="ghost" onClick={() => navigate(`/admin/carnet/${commande.id}`)}>
                  Ouvrir la commande →
                </Btn>
              </Panel>
            </Stagger>
          )}

          {/* ── Coordonnées ── */}
          <Stagger $i={2}>
            <Panel>
              <PanelHead>
                <PanelTitle $sm>Coordonnées</PanelTitle>
                {!editCoords && (
                  <Btn $variant="ghost" $size="sm" onClick={() => setEditCoords({ name: quote.name, email: quote.email, phone: quote.phone || '' })}>
                    ✏️ Modifier
                  </Btn>
                )}
              </PanelHead>

              {editCoords ? (
                <>
                  <Row $cols="1fr 1fr">
                    <Field><Label>Nom</Label><Input value={editCoords.name || ''} onChange={(e) => setEditCoords((p) => ({ ...p, name: e.target.value }))} /></Field>
                    <Field><Label>Téléphone</Label><Input type="tel" value={editCoords.phone || ''} onChange={(e) => setEditCoords((p) => ({ ...p, phone: e.target.value }))} /></Field>
                  </Row>
                  <Field><Label>Email</Label><Input type="email" value={editCoords.email || ''} onChange={(e) => setEditCoords((p) => ({ ...p, email: e.target.value }))} /></Field>
                  <Cluster>
                    <Btn $size="sm" onClick={saveCoords}>✓ Enregistrer</Btn>
                    <Btn $variant="ghost" $size="sm" onClick={() => setEditCoords(null)}>Annuler</Btn>
                  </Cluster>
                </>
              ) : (
                <DefList>
                  <DefRow><dt>Nom</dt><dd>{quote.name}</dd></DefRow>
                  <DefRow><dt>Email</dt><dd>{quote.email}</dd></DefRow>
                  <DefRow><dt>Téléphone</dt><dd>{quote.phone || '—'}</dd></DefRow>
                </DefList>
              )}
            </Panel>
          </Stagger>

          {/* ── Détails de la demande ── */}
          {(autresDetails.length > 0 || messageLibre || quote.message) && (
            <Stagger $i={3}>
              <Panel>
                <PanelHead>
                  <PanelTitle $sm>Détails de la demande</PanelTitle>
                  {adresse && (
                    <Btn
                      as="a"
                      href={adresse.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      $variant="ghost"
                      $size="sm"
                      title={`Ouvrir « ${adresse.texte} » dans Plans`}
                    >
                      🗺 Voir sur le plan
                    </Btn>
                  )}
                </PanelHead>
                {autresDetails.length > 0 && (
                  <DefList style={{ marginBottom: messageLibre ? 14 : 0 }}>
                    {autresDetails.map((d, i) => (
                      <DefRow key={i}>
                        <dt>{d.key}</dt>
                        <dd>
                          {d.value && estChampAdresse(d.key) ? (
                            <MapLink
                              href={mapsUrl(d.value)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Ouvrir dans Plans"
                            >
                              {d.value}
                            </MapLink>
                          ) : (
                            d.value || '—'
                          )}
                        </dd>
                      </DefRow>
                    ))}
                  </DefList>
                )}
                {messageLibre && (
                  <>
                    <Label>Message du client</Label>
                    <Pre $max="200px">{messageLibre.value}</Pre>
                  </>
                )}
                {autresDetails.length === 0 && !messageLibre && quote.message && (
                  <Pre $max="220px">{quote.message}</Pre>
                )}
              </Panel>
            </Stagger>
          )}

          {/* ── Consultations client ── */}
          <Stagger $i={4}>
            <Panel>
              <PanelHead>
                <PanelTitle $sm>👁 Page de suivi</PanelTitle>
                <Btn $variant="quiet" $size="sm" onClick={refreshViews}>↻ Actualiser</Btn>
              </PanelHead>
              <ViewsBox $any={vues.apres > 0}>
                {views.length === 0 ? (
                  <ViewLine>Le client n'a pas encore consulté sa page de suivi.</ViewLine>
                ) : (
                  <>
                    <ViewsHead $on={vues.apres > 0}>
                      {vues.apres === null
                        ? `Consultée ${vues.total} fois`
                        : vues.apres > 0
                        ? `Devis lu — ${vues.apres} consultation${vues.apres > 1 ? 's' : ''} depuis l'envoi`
                        : 'Devis pas encore lu depuis son envoi'}
                    </ViewsHead>

                    {vues.apres !== null && vues.avant > 0 && (
                      <ViewLine style={{ marginBottom: 6 }}>
                        {vues.avant} consultation{vues.avant > 1 ? 's' : ''} avant l'envoi du devis
                      </ViewLine>
                    )}
                    {vues.apres === null && quote.devis_content && (
                      <ViewLine style={{ marginBottom: 6 }}>
                        Envoi non horodaté : impossible de dire lesquelles sont postérieures.
                      </ViewLine>
                    )}

                    {views.slice(0, 3).map((v, i) => {
                      const apres = quote.devis_sent_at && new Date(v.viewed_at) > new Date(quote.devis_sent_at)
                      return (
                        <ViewLine key={i} style={apres ? { color: t.green, fontWeight: 540 } : undefined}>
                          {fmtDateTime(v.viewed_at)}{apres ? ' · après envoi' : ''}
                        </ViewLine>
                      )
                    })}
                    {views.length > 3 && <ViewLine style={{ opacity: 0.7 }}>+{views.length - 3} autre(s)</ViewLine>}
                  </>
                )}
              </ViewsBox>
            </Panel>
          </Stagger>
        </div>

        {/* ── Colonne droite : générateur ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Stagger $i={1}>
            <Panel $accent>
              <PanelHead><PanelTitle>💰 Générer le devis</PanelTitle></PanelHead>

              {lignes.map((l, i) => {
                const c = calcLigne(l)
                const f = isFourrage(l.produit)
                return (
                  <LigneCard key={i}>
                    <LigneName>
                      {l.produit}
                      {f && <Pill $tone="amber">Taxe fourrage</Pill>}
                    </LigneName>
                    <Row $cols="1fr 1fr 1fr 1fr" $gap="10px">
                      <Field $flush>
                        <Label>Poids (T)</Label>
                        <Input type="number" step="1" value={l.poids} onChange={(e) => updateLigne(i, 'poids', e.target.value)} placeholder="23" />
                      </Field>
                      <Field $flush>
                        <Label>Bottes</Label>
                        <Input type="number" step="1" value={l.bottes} onChange={(e) => updateLigne(i, 'bottes', e.target.value)} placeholder="—" />
                      </Field>
                      <Field $flush>
                        <Label>Prix /100kg</Label>
                        <Input type="number" step="0.01" value={l.prix} onChange={(e) => updateLigne(i, 'prix', e.target.value)} placeholder="18.50" />
                      </Field>
                      <Field $flush>
                        <Label>Taxe fourrage /100kg</Label>
                        <Input type="number" step="0.01" value={l.taxe}
                          onChange={(e) => updateLigne(i, 'taxe', e.target.value)}
                          placeholder={f ? '2.30' : '0'} disabled={!f} />
                      </Field>
                    </Row>
                    {l.poids && l.prix && (
                      <LigneCalc>
                        <span>{c.pr.toFixed(2)} × {c.p} T × 10{f && c.tx > 0 ? ` + taxe ${c.tx.toFixed(2)} × ${c.p} T × 10` : ''}</span>
                        <b>{montant(c.total)}</b>
                      </LigneCalc>
                    )}
                  </LigneCard>
                )
              })}

              {lignesRemplies && lignes.length > 1 && (
                <TotalBar>
                  <span>Total général HT</span>
                  <span>{montant(grandTotal)}</span>
                </TotalBar>
              )}

              {lignesRemplies && (
                <>
                  <Field>
                    <Label>Type de mail</Label>
                    <Select value={modeleId} onChange={(e) => setModeleId(e.target.value)}>
                      {modeles.map((m) => (
                        <option key={m.cle || m.id} value={m.cle || m.id}>{m.nom}</option>
                      ))}
                    </Select>
                  </Field>
                  <Field>
                    <Label>Aperçu du mail</Label>
                    <Pre $max="230px">{corpsDe(modele, quote, lignes)}</Pre>
                  </Field>
                </>
              )}

              {!lignesRemplies && (
                <Muted style={{ marginBottom: 14 }}>
                  Renseignez le poids et le prix de chaque produit pour débloquer l'aperçu et l'envoi.
                </Muted>
              )}

              <Cluster $gap="10px">
                {lignesRemplies && (
                  <Btn $variant="ghost" style={{ flex: 1 }} onClick={() => { window.location.href = mailtoDe(modele, quote, lignes) }}>
                    📧 Ouvrir dans la messagerie
                  </Btn>
                )}
                <Btn $variant="ghost" $size="sm" onClick={inscrireEnAttente}
                  title="Le client sera livré dès que la marchandise sera trouvée">
                  ⏳ Liste d'attente
                </Btn>
                <Btn $variant="gold" style={{ flex: 1 }} disabled={!lignesRemplies || saving} onClick={sauvegarderEtEnvoyer}>
                  {saving ? <><Spinner /> Envoi…</> : '✅ Enregistrer et envoyer'}
                </Btn>
              </Cluster>
            </Panel>
          </Stagger>

          {/* ── Devis déjà envoyé ── */}
          {quote.devis_content && (
            <Stagger $i={2}>
              <Panel>
                <PanelHead>
                  <PanelTitle $sm><Pill $tone="green">✅ Devis envoyé</Pill></PanelTitle>
                  <Btn $variant="ghost" $size="sm" onClick={() => { window.location.href = mailtoDevis(quote, quote.devis_content) }}>
                    📧 Renvoyer
                  </Btn>
                </PanelHead>
                <Pre $tone="green" $max="220px">{quote.devis_content}</Pre>
              </Panel>
            </Stagger>
          )}
        </div>
      </Row>

      {/* ── Coût de revient, tout en bas ── */}
      <Stagger $i={5} style={{ marginTop: 16 }}>
        <CoutLivraison
          destination={adresseDestination(autresDetails)}
          lignesDevis={lignes}
          vente={grandTotal}
          cible={{ table: 'quotes', id: quote.id }}
          coutEnregistre={quote.cout_revient}
          onEnregistre={(c) => setQuote((q) => ({ ...q, cout_revient: c }))}
        />
      </Stagger>
    </>
  )
}
